import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, User, Mail, Lock, Eye, EyeOff, Loader2, FileText, Building2, Phone } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useApi } from '../../services/api'
import EmailVerificationModal from './EmailVerificationModal'

interface RegisterModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  onSwitchToLogin: () => void
}

const registerSchema = yup.object({
  name: yup.string().min(2, 'Nome deve ter pelo menos 2 caracteres').required('Nome é obrigatório'),
  email: yup.string().email('Email inválido').required('Email é obrigatório'),
  document: yup.string().required('CPF ou CNPJ é obrigatório'),
  document_type: yup.string().oneOf(['CPF', 'CNPJ'], 'Tipo de documento inválido').required('Tipo de documento é obrigatório'),
  type: yup.string().oneOf(['individual', 'company'], 'Tipo de usuário inválido').required('Tipo de usuário é obrigatório'),
  phone: yup.string().test('phone-format', 'Telefone inválido (aceita fixo: (11) 3333-4444 ou celular: (11) 99999-9999)', function(value) {
    if (!value) return false
    // Remove todos os caracteres não numéricos
    const numbers = value.replace(/\D/g, '')
    // Verifica se tem 10 ou 11 dígitos (telefone fixo ou celular)
    return numbers.length === 10 || numbers.length === 11
  }).required('Telefone é obrigatório'),
  password: yup.string().min(6, 'Senha deve ter pelo menos 6 caracteres').required('Senha é obrigatória'),
  confirm_password: yup.string().oneOf([yup.ref('password')], 'Senhas devem ser iguais').required('Confirme sua senha'),
})

interface RegisterFormData {
  name: string
  email: string
  document: string
  document_type: 'CPF' | 'CNPJ'
  type: 'individual' | 'company'
  phone: string
  password: string
  confirm_password: string
}

const RegisterModal = ({ isOpen, onClose, onSuccess, onSwitchToLogin }: RegisterModalProps) => {
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showVerificationModal, setShowVerificationModal] = useState(false)
  const [registeredEmail, setRegisteredEmail] = useState('')
  const [documentType, setDocumentType] = useState<'CPF' | 'CNPJ'>('CPF')
  const [userType, setUserType] = useState<'individual' | 'company'>('individual')
  const { register: registerUser } = useApi()

  // Função para formatar telefone
  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '')
    if (numbers.length <= 2) return numbers
    if (numbers.length <= 6) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`
    if (numbers.length <= 10) {
      // Telefone fixo: (11) 3333-4444
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`
    }
    if (numbers.length <= 11) {
      // Celular: (11) 99999-9999
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`
    }
    return value // Se tiver mais de 11 dígitos, não formata
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<RegisterFormData>({
    resolver: yupResolver(registerSchema),
  })

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true)
    setError(null)

    try {
      // Remove confirm_password antes de enviar para a API
      const { confirm_password, ...registerData } = data
      
      console.log('🔍 Dados enviados para API:', registerData);
      const response = await registerUser(registerData)
      console.log('🔍 Resposta do registro:', response)
      
      // Se o registro foi bem-sucedido, sempre mostrar o modal de verificação
      if (response.message) {
        setRegisteredEmail(data.email)
        setShowVerificationModal(true)
        
        // Armazenar dados de registro para verificação pendente
        const registrationData = {
          email: data.email,
          name: data.name,
          document: data.document,
          document_type: data.document_type,
          type: data.type,
          phone: data.phone,
          timestamp: Date.now()
        }
        
        localStorage.setItem('pending_verification', JSON.stringify(registrationData))
        
        // Armazenar o user_id e token se foram fornecidos pelo backend
        if (response.user_id) {
          localStorage.setItem('user_id', response.user_id)
          console.log('✅ User ID armazenado')
        }
        
        if (response.verification_token) {
          localStorage.setItem('verification_token', response.verification_token)
          console.log('✅ Token de verificação armazenado:', response.verification_token.substring(0, 20) + '...')
        } else {
          console.log('❌ Token de verificação não foi fornecido pelo backend')
        }
      } else {
        // Caso o registro falhou
        throw new Error(response.message || 'Erro no registro')
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Erro no registro')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      reset()
      setError(null)
      onClose()
    }
  }

  const handleSwitchToLogin = () => {
    if (!isLoading) {
      handleClose()
      onSwitchToLogin()
    }
  }

  const handleVerificationSuccess = () => {
    setShowVerificationModal(false)
    handleClose()
    onSuccess()
  }

  const handleVerificationClose = () => {
    setShowVerificationModal(false)
    // Usuário pode fechar o modal de verificação e tentar novamente
  }

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={handleClose}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6 md:p-8 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6 md:mb-8">
                <div className="flex items-center space-x-2 md:space-x-3">
                  <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center">
                    <User className="w-4 h-4 md:w-5 md:h-5 text-white" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900">Criar Conta</h2>
                </div>
                <button
                  onClick={handleClose}
                  disabled={isLoading}
                  className="p-1 md:p-2 text-gray-400 hover:text-gray-600 transition-colors duration-200 disabled:opacity-50"
                >
                  <X className="w-4 h-4 md:w-5 md:h-5" />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 md:space-y-6">
                {/* Nome Completo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nome Completo
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-400" />
                    <input
                      {...register('name')}
                      type="text"
                      placeholder="João Silva"
                      className="w-full pl-9 md:pl-10 pr-4 py-2.5 md:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 text-sm md:text-base"
                      disabled={isLoading}
                    />
                  </div>
                  {errors.name && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-2 text-sm text-red-600"
                    >
                      {errors.name.message}
                    </motion.p>
                  )}
                </div>

                {/* Tipo de Usuário */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Usuário
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setUserType('individual')
                        setDocumentType('CPF')
                      }}
                      className={`flex items-center justify-center space-x-2 py-2.5 px-4 rounded-xl border transition-all duration-200 ${
                        userType === 'individual'
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-300 text-gray-600 hover:border-gray-400'
                      }`}
                      disabled={isLoading}
                    >
                      <User className="w-4 h-4" />
                      <span className="text-sm font-medium">Pessoa Física</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setUserType('company')
                        setDocumentType('CNPJ')
                      }}
                      className={`flex items-center justify-center space-x-2 py-2.5 px-4 rounded-xl border transition-all duration-200 ${
                        userType === 'company'
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-300 text-gray-600 hover:border-gray-400'
                      }`}
                      disabled={isLoading}
                    >
                      <Building2 className="w-4 h-4" />
                      <span className="text-sm font-medium">Pessoa Jurídica</span>
                    </button>
                  </div>
                  <input type="hidden" {...register('type')} value={userType} />
                  <input type="hidden" {...register('document_type')} value={documentType} />
                  {errors.type && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-2 text-sm text-red-600"
                    >
                      {errors.type.message}
                    </motion.p>
                  )}
                </div>

                {/* CPF/CNPJ */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {documentType === 'CPF' ? 'CPF' : 'CNPJ'}
                  </label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-400" />
                    <input
                      {...register('document')}
                      type="text"
                      placeholder={documentType === 'CPF' ? '000.000.000-00' : '00.000.000/0000-00'}
                      className="w-full pl-9 md:pl-10 pr-4 py-2.5 md:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 text-sm md:text-base"
                      disabled={isLoading}
                    />
                  </div>
                  {errors.document && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-2 text-sm text-red-600"
                    >
                      {errors.document.message}
                    </motion.p>
                  )}
                </div>

                {/* Telefone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telefone
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-400" />
                    <input
                      {...register('phone')}
                      type="tel"
                      placeholder="(11) 99999-9999 ou (11) 3333-4444"
                      maxLength={15}
                      onChange={(e) => {
                        const formatted = formatPhone(e.target.value)
                        e.target.value = formatted
                      }}
                      className="w-full pl-9 md:pl-10 pr-4 py-2.5 md:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 text-sm md:text-base"
                      disabled={isLoading}
                    />
                  </div>
                  {errors.phone && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-2 text-sm text-red-600"
                    >
                      {errors.phone.message}
                    </motion.p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-400" />
                    <input
                      {...register('email')}
                      type="email"
                      placeholder="joao@exemplo.com"
                      className="w-full pl-9 md:pl-10 pr-4 py-2.5 md:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 text-sm md:text-base"
                      disabled={isLoading}
                    />
                  </div>
                  {errors.email && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-2 text-sm text-red-600"
                    >
                      {errors.email.message}
                    </motion.p>
                  )}
                </div>

                {/* Senha */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Senha
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-400" />
                    <input
                      {...register('password')}
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Sua senha"
                      className="w-full pl-9 md:pl-10 pr-12 py-2.5 md:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 text-sm md:text-base"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                      disabled={isLoading}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {errors.password && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-2 text-sm text-red-600"
                    >
                      {errors.password.message}
                    </motion.p>
                  )}
                </div>

                {/* Confirmar Senha */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirmar Senha
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-400" />
                    <input
                      {...register('confirm_password')}
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirme sua senha"
                      className="w-full pl-9 md:pl-10 pr-12 py-2.5 md:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 text-sm md:text-base"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                      disabled={isLoading}
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {errors.confirm_password && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-2 text-sm text-red-600"
                    >
                      {errors.confirm_password.message}
                    </motion.p>
                  )}
                </div>

                {/* Error Message */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="p-4 bg-red-50 border border-red-200 rounded-xl"
                  >
                    <p className="text-sm text-red-600">{error}</p>
                  </motion.div>
                )}

                {/* Submit Button */}
                <motion.button
                  type="submit"
                  disabled={isLoading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-gradient-to-r from-primary-500 to-accent-500 text-white font-semibold py-2.5 md:py-3 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-sm md:text-base"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Criando conta...</span>
                    </>
                  ) : (
                    <span>Criar Conta</span>
                  )}
                </motion.button>
              </form>

              {/* Footer */}
              <div className="mt-6 md:mt-8 text-center">
                <p className="text-xs md:text-sm text-gray-600">
                  Já tem uma conta?{' '}
                  <button
                    onClick={handleSwitchToLogin}
                    className="text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200"
                  >
                    Faça login
                  </button>
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Email Verification Modal */}
      <EmailVerificationModal
        isOpen={showVerificationModal}
        onClose={handleVerificationClose}
        onSuccess={handleVerificationSuccess}
        email={registeredEmail}

      />
    </>
  )
}

export default RegisterModal
